import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { qualityForPath, seoDescription } from "../data/businessQuality";
import { useConceptLifecycleStatus } from "../hooks/useConceptLifecycleStatus";
import { robotsContentFor } from "../data/robotsPolicy";

const CORE_SEO = {
  "/": ["MySiteCare — Website Responsibility for EU & International Businesses", "Plan-defined website creation, maintenance, monitoring and technical responsibility for EU and international businesses."],
  "/about": ["About MySiteCare — EU & International", "Learn how MySiteCare defines website responsibility, service scope and ownership for EU and international clients."],
  "/plans": ["Website Plans — MySiteCare EU & International", "Compare MySiteCare website ownership and subscription plans for EU and international businesses."],
  "/faq": ["Frequently Asked Questions — MySiteCare EU & International", "Answers about MySiteCare website plans, scope, ownership, payments and ongoing responsibility."],
  "/contact": ["Contact MySiteCare — EU & International", "Contact MySiteCare to discuss a website project or the level of technical responsibility your business needs."],
  "/why-this-matters": ["Why Website Responsibility Matters — MySiteCare", "See why clear website scope, dependable maintenance and defined technical responsibility matter to businesses."],
  "/clear-terms": ["Payment Terms — MySiteCare EU & International", "Plain-language payment, subscription, ownership, suspension and service-continuity terms for EU and international clients."],
  "/refund-cancellation-policy": ["Refund & Cancellation Policy — MySiteCare", "Current cancellation, refund, and billing-correction rules for MySiteCare services."],
  "/what-we-do": ["What MySiteCare Does — EU & International", "The plan-dependent website work and technical responsibilities MySiteCare can provide."],
  "/what-we-dont": ["What MySiteCare Does Not Do — EU & International", "Clear exclusions and boundaries for MySiteCare website services."],
  "/terms-and-conditions": ["Terms and Conditions — MySiteCare EU & International", "Terms and conditions for MySiteCare website services for EU and international clients."],
  "/privacy-policy": ["Privacy Policy — MySiteCare EU & International", "How MySiteCare handles personal information and website inquiries."],
  "/cookie-policy": ["Cookie Policy — MySiteCare EU & International", "Information about cookies used by the MySiteCare website."],
  "/business-information": ["Business Information — MySiteCare EU & International", "Verified legal identity, registration and EU contact information for MySiteCare Website Application Services."],
  "/ph": ["MySiteCare Philippines — Managed Business Websites", "Managed website plans for Philippine businesses, with clear responsibility for hosting, updates, maintenance and ongoing support."],
  "/ph/about": ["About MySiteCare Philippines", "Learn how MySiteCare approaches managed business websites, clear ownership of technical work and ongoing website care."],
  "/ph/plans": ["Website Plans — MySiteCare Philippines", "Compare MySiteCare managed website plans for Philippine businesses and see the responsibilities included with each option."],
  "/ph/faq": ["Frequently Asked Questions — MySiteCare Philippines", "Answers about MySiteCare website plans, support, updates, hosting and ongoing responsibility."],
  "/ph/contact": ["Contact MySiteCare Philippines", "Contact MySiteCare to discuss a managed website, an existing concept or the support your business needs."],
  "/ph/why-this-matters": ["Why Your Business Website Matters — MySiteCare Philippines", "See why clear website ownership, dependable maintenance and a credible online presence matter to Philippine businesses."],
  "/ph/clear-terms": ["Payment Terms — MySiteCare Philippines", "Plain-language rules for MySiteCare payments, previews, ownership, suspension and service continuity."],
  "/ph/refund-cancellation-policy": ["Refund & Cancellation Policy — MySiteCare Philippines", "Current cancellation, refund, and billing-correction rules for MySiteCare services."],
  "/ph/what-we-do": ["What MySiteCare Does — Philippines", "The plan-dependent website work and technical responsibilities MySiteCare can provide."],
  "/ph/what-we-dont": ["What MySiteCare Does Not Do — Philippines", "Clear exclusions and boundaries for MySiteCare website services."],
  "/ph/terms-and-conditions": ["Terms and Conditions — MySiteCare Philippines", "Terms and conditions for MySiteCare website services."],
  "/ph/privacy-policy": ["Privacy Policy — MySiteCare Philippines", "How MySiteCare handles personal information and website inquiries."],
  "/ph/cookie-policy": ["Cookie Policy — MySiteCare Philippines", "Information about cookies used by the MySiteCare website."],
  "/ph/business-information": ["Business Information — MySiteCare Philippines", "Verified legal identity, Philippine registration and contact information for MySiteCare Website Application Services."],
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  const created = !element;
  const previousContent = element?.getAttribute("content") ?? null;
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  return { element, created, previousContent };
};

export default function BusinessSeo() {
  const { pathname } = useLocation();
  const { status } = useConceptLifecycleStatus(pathname);

  // Robots noindex — kept in its own effect (separate from the title/OG/schema effect
  // below) so it can react to `status` resolving asynchronously without re-running the
  // rest of the SEO tag setup.
  useEffect(() => {
    const content = robotsContentFor(pathname, status);
    if (!content) return undefined;

    const record = upsertMeta('meta[name="robots"]', { name: "robots", content });
    return () => {
      if (record.created) record.element.remove();
      else if (record.previousContent === null) record.element.removeAttribute("content");
      else record.element.setAttribute("content", record.previousContent);
    };
  }, [pathname, status]);

  useEffect(() => {
    const businessItem = qualityForPath(pathname);
    const normalized = pathname.toLowerCase().replace(/\/$/, "") || "/";
    const isPhilippines = normalized === "/ph" || normalized.startsWith("/ph/");
    const planName = /^\/(?:ph\/)?plans\//.test(normalized)
      ? normalized.split("/").pop().split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
      : null;
    const marketLabel = isPhilippines ? "MySiteCare Philippines" : "MySiteCare EU & International";
    const core = CORE_SEO[normalized] || (planName ? [`${planName} — ${marketLabel}`, `Review the ${planName} website option, including its scope, ownership position and ongoing service responsibilities.`] : null);
    if (!businessItem && !core) return undefined;

    const title = businessItem ? `${businessItem.name} — ${businessItem.locality} | MySiteCare Concept` : core[0];
    const description = businessItem ? seoDescription(businessItem) : core[1];
    const canonicalPath = businessItem?.path || normalized;
    const canonicalUrl = `https://mysitecare.eu${canonicalPath}`;
    const previousTitle = document.title;
    document.title = title;

    const descriptionMeta = upsertMeta('meta[name="description"]', { name: "description", content: description });
    const ogTitle = upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    const ogDescription = upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    const ogType = upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    const ogUrl = upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    const twitterCard = upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    const previewImage = document.querySelector("main img[src], #top img[src], img[src]");
    const ogImage = previewImage
      ? upsertMeta('meta[property="og:image"]', { property: "og:image", content: new URL(previewImage.getAttribute("src"), window.location.origin).href })
      : null;

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const previousCanonical = canonical?.getAttribute("href") ?? null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.mscBusinessSchema = "true";
    const websiteUrl = isPhilippines ? "https://mysitecare.eu/ph" : "https://mysitecare.eu/";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: canonicalUrl,
      inLanguage: ["en", "tl"],
      dateModified: businessItem?.verifiedOn || "2026-08-20",
      isPartOf: { "@type": "WebSite", name: "MySiteCare", url: websiteUrl },
      about: businessItem ? {
        "@type": "LocalBusiness",
        name: businessItem.name,
        description: `${businessItem.category} serving ${businessItem.locality}.`,
        areaServed: { "@type": "Place", name: businessItem.locality },
      } : { "@type": "Organization", name: "MySiteCare" },
      publisher: { "@type": "Organization", name: "MySiteCare", url: websiteUrl },
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      [descriptionMeta, ogTitle, ogDescription, ogType, ogUrl, twitterCard, ogImage].filter(Boolean).forEach(record => {
        if (record.created) record.element.remove();
        else if (record.previousContent === null) record.element.removeAttribute("content");
        else record.element.setAttribute("content", record.previousContent);
      });
      if (canonicalCreated) canonical.remove();
      else if (previousCanonical === null) canonical.removeAttribute("href");
      else canonical.setAttribute("href", previousCanonical);
      schema.remove();
    };
  }, [pathname]);

  return null;
}
